import { useMemo } from 'react'

import type { api } from './api'
import { useSession } from './SessionContext'

/* ============================================================================
   LINE OA (หน้า /admin/line) — type / ตัวเรียก API
   backend: src/routes/adminLine.js · ตาราง: sql/mssql/001_line_register.sql

   ⚠️ **QR และตัว OA เป็นของกลาง ใช้ร่วมกันทุกบริษัท** เพราะ Messaging API channel
      มีตัวเดียว (LINE_CHANNEL_ACCESS_TOKEN ไม่ได้แยกตามบริษัท) ที่แยกกันจริงคือ
      LINE Login channel / LIFF และตารางลงทะเบียนที่อยู่คนละ DB
      → หน้าเว็บต้องเขียนบอก ไม่งั้นคนจะรายงานว่า "QR ของ simit ผิด"

   ⚠️ เวลาในไฟล์นี้เป็น **UTC** ทั้งหมด (registeredAt / revokedAt / createdAt)
      ต่างจาก offsite.ts ที่เป็นเวลาไทย — ตารางลงทะเบียนเก็บด้วย SYSUTCDATETIME()
      ตั้งใจ เพราะเป็นเวลาที่ใช้ตรวจสอบย้อนหลัง ไม่ใช่เวลาเชิงธุรกิจ
      → แสดงผลด้วย formatUtcAsThai() ของ lib/datetime.ts เท่านั้น ห้ามใช้ formatDateTime
   ============================================================================ */

export const LINE_BASE = '/api/admin/line'

/** ผลลัพธ์ที่แบ่งหน้า — กติกาเดียวกับทั้งระบบ (backend/src/lib/paging.js) */
export type Paged<T> = { total: number; page: number; pageSize: number } & T
export type PageOpts = { page?: number; pageSize?: number; sort?: string }

/**
 * ทุกก้อนที่ยิง LINE API คืนรูปนี้ — ล้มแล้วไม่ throw
 * หน้าเว็บต้องแสดงตารางลงทะเบียนได้ต่อแม้ LINE จะล่มหรือ token หมดอายุ
 */
type MaybeFailed<T> = ({ ok: true } & T) | { ok: false; error: string; status?: number | null }

export type OaInfo = MaybeFailed<{
  basicId: string | null
  displayName: string | null
  pictureUrl: string | null
  /** 'bot' = webhook ทำงาน · 'chat' = คนตอบเอง แล้วคำว่า "ลาออก" จะไม่ถูกประมวลผล */
  chatMode: string | null
  markAsReadMode: string | null
  premiumId: string | null
  /** ข้อความที่จะถูกเข้ารหัสลง QR */
  addFriendUrl: string | null
}>

export type OaQuota = MaybeFailed<{
  /** 'limited' = มีเพดาน · 'none' = ไม่จำกัด (limit จะเป็น null ต้องแยกจาก 0) */
  type: string | null
  limit: number | null
  used: number
}>

export type MenuKind = {
  kind: 'register' | 'main' | 'basic'
  name: string
  richMenuId: string | null
  size: string | null
  chatBarText: string | null
  deployed: boolean
  /** ชื่อซ้ำ = runtime หาเมนูจากชื่อแล้วได้อันไหนก็ไม่รู้ → ผูกเมนูผิดแบบสุ่ม */
  duplicated: boolean
  isDefault: boolean
}

export type MenuStatus = MaybeFailed<{
  defaultRichMenuId: string | null
  /** default ต้องเป็นเมนู register เท่านั้น ผิดเมื่อไรคนใหม่หาปุ่มลงทะเบียนไม่เจอ */
  defaultIsRegister: boolean
  menus: MenuKind[]
  otherCount: number
  duplicatedNames: string[]
}>

export type Overview = { oa: OaInfo; quota: OaQuota; menus: MenuStatus }

export type LineRegistration = {
  id: number
  lineUserId: string
  lineDisplayName: string | null
  linePictureUrl: string | null
  empCode: string
  empName: string | null
  department: string | null
  position: string | null
  status: 'active' | 'revoked'
  liffId: string | null
  /** UTC — ใช้ formatUtcAsThai() */
  registeredAt: string | null
  revokedAt: string | null
  revokeReason: string | null
  /** null = ผู้ใช้ยกเลิกเอง (หรือ DB ยังไม่ได้รัน migration 008) */
  revokedBy: string | null
  /** พนักงานคนนี้ผูกไว้กี่บัญชี — 1 คนผูกได้หลาย LINE โดยตั้งใจ (ดู DDL 001) */
  activeAccounts: number
}

export type AttemptResult = 'ok' | 'not_found' | 'already_registered' | 'invalid_input' | 'error'

export type LineAttempt = {
  id: number
  /** UTC */
  createdAt: string | null
  lineUserId: string | null
  empCode: string | null
  /** เก็บแค่ 4 ตัวท้ายตั้งแต่ตอนบันทึก (ตั้งใจ — DDL 001) */
  citizenIdLast4: string | null
  result: AttemptResult | string
  message: string | null
}

export const ATTEMPT_LABEL: Record<string, string> = {
  ok: 'สำเร็จ',
  not_found: 'ไม่พบพนักงาน',
  already_registered: 'ผูกไปแล้ว',
  invalid_input: 'กรอกไม่ถูกต้อง',
  error: 'ระบบผิดพลาด',
}

const query = (params: Record<string, string | undefined>) => {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') q.set(k, v)
  const s = q.toString()
  return s ? `?${s}` : ''
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) })
const num = (n: number | undefined) => (n === undefined ? undefined : String(n))

function listOf<T>(res: unknown, key: string): T[] {
  if (Array.isArray(res)) return res as T[]
  const value = res && typeof res === 'object' ? (res as Record<string, unknown>)[key] : undefined
  return Array.isArray(value) ? (value as T[]) : []
}

export function adminLineApi(call: typeof api) {
  return {
    /** refresh = บังคับถาม LINE ใหม่ ไม่เอาจาก cache ฝั่ง backend */
    overview: (refresh = false) =>
      call<Overview>(`${LINE_BASE}/overview${refresh ? '?refresh=1' : ''}`),

    listRegistrations: (opts: { status: 'active' | 'revoked' | 'all'; q?: string } & PageOpts) =>
      call<Paged<{ registrations?: LineRegistration[] }>>(
        `${LINE_BASE}/registrations${query({
          status: opts.status,
          q: opts.q,
          page: num(opts.page),
          pageSize: num(opts.pageSize),
          sort: opts.sort,
        })}`,
      ).then((res) => ({
        registrations: listOf<LineRegistration>(res, 'registrations'),
        total: res?.total ?? 0,
        page: res?.page ?? 1,
        pageSize: res?.pageSize ?? 10,
      })),

    /** backend บังคับว่าต้องมีเหตุผล — ประวัติที่ไม่มีเหตุผลตอบอะไรไม่ได้ตอนย้อนดู */
    revoke: (lineUserId: string, reason: string) =>
      call<{ revoked: number; empCode: string }>(
        `${LINE_BASE}/registrations/${encodeURIComponent(lineUserId)}/revoke`,
        { method: 'POST', ...json({ reason }) },
      ),

    listAttempts: (opts: { days?: number; failedOnly?: boolean } & PageOpts) =>
      call<Paged<{ attempts?: LineAttempt[]; days?: number }>>(
        `${LINE_BASE}/attempts${query({
          days: num(opts.days),
          failedOnly: opts.failedOnly ? 'true' : undefined,
          page: num(opts.page),
          pageSize: num(opts.pageSize),
          sort: opts.sort,
        })}`,
      ).then((res) => ({
        attempts: listOf<LineAttempt>(res, 'attempts'),
        total: res?.total ?? 0,
        page: res?.page ?? 1,
        pageSize: res?.pageSize ?? 10,
      })),
  }
}

export type AdminLineApi = ReturnType<typeof adminLineApi>

export function useAdminLine(): AdminLineApi {
  const { call } = useSession()
  return useMemo(() => adminLineApi(call), [call])
}
